import React, { useMemo } from 'react';
import { Box } from '@mui/material';

export enum AnchorPosition {
  CENTER = 'center',
  CENTER_FULL = 'centerFull',
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  TOP_LEFT = 'topLeft',
  TOP_RIGHT = 'topRight',
  BOTTOM_LEFT = 'bottomLeft',
  BOTTOM_RIGHT = 'bottomRight',
}

interface ResponsiveLayoutProps {
  children: React.ReactNode;
  anchor?: AnchorPosition;
  width?: string;
  padding?: number | string;
}

/**
 * Responsive positioning wrapper that fills the remaining viewport height.
 *
 * Height is driven by the CSS custom properties published by
 * useBrightChromeObserver (called once at the app root), so this component
 * needs no observers, event listeners, or React state of its own.
 */
const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  anchor = AnchorPosition.CENTER,
  width = 'default',
  padding = 0,
}) => {
  const alignmentStyles = useMemo(() => {
    switch (anchor) {
      case AnchorPosition.BOTTOM:
        return { alignItems: 'flex-end', justifyContent: 'center' };
      case AnchorPosition.LEFT:
        return { alignItems: 'center', justifyContent: 'flex-start' };
      case AnchorPosition.RIGHT:
        return { alignItems: 'center', justifyContent: 'flex-end' };
      case AnchorPosition.BOTTOM_LEFT:
        return { alignItems: 'flex-end', justifyContent: 'flex-start' };
      case AnchorPosition.BOTTOM_RIGHT:
        return { alignItems: 'flex-end', justifyContent: 'flex-end' };
      case AnchorPosition.TOP:
        return {
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexDirection: 'column',
        };
      case AnchorPosition.TOP_LEFT:
        return {
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          flexDirection: 'column',
        };
      case AnchorPosition.TOP_RIGHT:
        return {
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          flexDirection: 'column',
        };
      case AnchorPosition.CENTER:
      default:
        return { alignItems: 'center', justifyContent: 'center' };
    }
  }, [anchor]);

  return (
    <Box
      sx={{
        minHeight:
          'calc(100vh - var(--bright-header-height, 0px) - var(--bright-footer-height, 0px))',
        display: 'flex',
        padding,
        ...alignmentStyles,
        marginTop: '-7px', // Account for &>after element on bright mega menu (out of our control, for now)
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          width:
            anchor === AnchorPosition.LEFT || anchor === AnchorPosition.RIGHT
              ? 'auto'
              : width,
          ...([
            AnchorPosition.TOP,
            AnchorPosition.TOP_RIGHT,
            AnchorPosition.TOP_LEFT,
          ].includes(anchor)
            ? { display: 'flex', flex: 1 }
            : {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ResponsiveLayout;
